/**
 * Organization Context for White-Label Support
 * =============================================
 * Provides organization filtering across the admin dashboard.
 *
 * USAGE:
 * - Wrap your app with <OrganizationProvider>
 * - Use useOrganization() hook in components
 * - Filter API calls with selectedOrganization
 *
 * WHITE-LABEL CLIENTS:
 * - NASENI (Nigeria)
 * - Future clients can be added to organizations list
 *
 * ADMIN USERS:
 * - Super admins see all organizations
 * - Client admins see only their organization
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const OrganizationContext = createContext();

// Default organizations - will be loaded from API
const DEFAULT_ORGANIZATIONS = [
  { id: 'all', name: 'All Organizations', code: 'ALL' },
  { id: 1, name: 'GreenFlow', code: 'GREENFLOW' },
  { id: 2, name: 'NASENI', code: 'NASENI' }
];

export const OrganizationProvider = ({ children }) => {
  const [organizations, setOrganizations] = useState(DEFAULT_ORGANIZATIONS);
  const [selectedOrganization, setSelectedOrganization] = useState('all');
  const [loading, setLoading] = useState(true);
  const [userOrganization, setUserOrganization] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(true);

  // Load organizations from API
  const loadOrganizations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch('/api/organizations', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.organizations && data.organizations.length > 0) {
          setOrganizations([
            { id: 'all', name: 'All Organizations', code: 'ALL' },
            ...data.organizations
          ]);
        }

        // Check user's organization assignment
        if (data.user_organization) {
          setUserOrganization(data.user_organization);
          setIsSuperAdmin(false);
          setSelectedOrganization(data.user_organization.id);
        } else {
          setIsSuperAdmin(true);
        }
      }
    } catch (error) {
      console.log('Organizations API not available, using defaults');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  // Persist selection to localStorage
  useEffect(() => {
    if (isSuperAdmin) {
      localStorage.setItem('greenflow-selected-org', selectedOrganization);
    }
  }, [selectedOrganization, isSuperAdmin]);

  // Load persisted selection on mount
  useEffect(() => {
    if (isSuperAdmin) {
      const saved = localStorage.getItem('greenflow-selected-org');
      if (saved) {
        setSelectedOrganization(saved);
      }
    }
  }, [isSuperAdmin]);

  // Get query params for API calls
  const getOrgQueryParams = useCallback(() => {
    if (selectedOrganization === 'all') {
      return {};
    }
    return { organization_id: selectedOrganization };
  }, [selectedOrganization]);

  // Get query string for API calls
  const getOrgQueryString = useCallback(() => {
    if (selectedOrganization === 'all') {
      return '';
    }
    return `organization_id=${selectedOrganization}`;
  }, [selectedOrganization]);

  // Append org filter to existing URL
  const appendOrgFilter = useCallback((url) => {
    if (selectedOrganization === 'all') {
      return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}organization_id=${selectedOrganization}`;
  }, [selectedOrganization]);

  // Get current organization name
  const getCurrentOrgName = useCallback(() => {
    const org = organizations.find(o => String(o.id) === String(selectedOrganization));
    return org ? org.name : 'All Organizations';
  }, [organizations, selectedOrganization]);

  const value = {
    // State
    organizations,
    selectedOrganization,
    userOrganization,
    isSuperAdmin,
    loading,

    // Actions
    setSelectedOrganization: isSuperAdmin ? setSelectedOrganization : () => {},
    refreshOrganizations: loadOrganizations,

    // Helpers
    getOrgQueryParams,
    getOrgQueryString,
    appendOrgFilter,
    getCurrentOrgName,

    // Check if filtering is active
    isFiltering: selectedOrganization !== 'all'
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    // Return safe defaults if used outside provider (graceful degradation)
    return {
      organizations: DEFAULT_ORGANIZATIONS,
      selectedOrganization: 'all',
      userOrganization: null,
      isSuperAdmin: true,
      loading: false,
      setSelectedOrganization: () => {},
      refreshOrganizations: () => {},
      getOrgQueryParams: () => ({}),
      getOrgQueryString: () => '',
      appendOrgFilter: (url) => url,
      getCurrentOrgName: () => 'All Organizations',
      isFiltering: false
    };
  }
  return context;
};

export default OrganizationContext;
